export const TOPIC_LEARNING_STATE = {
	notStarted: "not_started",
	started: "started",
	completed: "completed",
} as const;

export type TopicLearningState =
	(typeof TOPIC_LEARNING_STATE)[keyof typeof TOPIC_LEARNING_STATE];

export const TOPIC_PICKER_SCOPE = {
	learned: "learned",
	all: "all",
} as const;

export type TopicPickerScope =
	(typeof TOPIC_PICKER_SCOPE)[keyof typeof TOPIC_PICKER_SCOPE];

export const TOPIC_PICKER_ELIGIBILITY = {
	all: "all",
	started: "started",
	completed: "completed",
} as const;

export type TopicPickerEligibility =
	(typeof TOPIC_PICKER_ELIGIBILITY)[keyof typeof TOPIC_PICKER_ELIGIBILITY];

export const TOPIC_PICKER_INELIGIBLE_DISPLAY = {
	disabled: "disabled",
	hidden: "hidden",
} as const;

export type TopicPickerIneligibleDisplay =
	(typeof TOPIC_PICKER_INELIGIBLE_DISPLAY)[keyof typeof TOPIC_PICKER_INELIGIBLE_DISPLAY];

export interface TopicPickerProgressSnapshot {
	startedTopicIds: Set<string>;
	completedTopicIds: Set<string>;
}

export type TopicPickerRowState =
	| "selectable"
	| "disabled_incomplete"
	| "hidden";

export function resolveTopicIdsForPickerEligibility(params: {
	eligibility: TopicPickerEligibility;
	progress: TopicPickerProgressSnapshot;
}): Set<string> {
	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.all) {
		return new Set([
			...params.progress.startedTopicIds,
			...params.progress.completedTopicIds,
		]);
	}
	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.started) {
		return new Set(params.progress.startedTopicIds);
	}
	return new Set(params.progress.completedTopicIds);
}

export function isTopicSelectableForPickerEligibility(params: {
	topicId: string;
	eligibility: TopicPickerEligibility;
	progress: TopicPickerProgressSnapshot;
}): boolean {
	const allowed = resolveTopicIdsForPickerEligibility({
		eligibility: params.eligibility,
		progress: params.progress,
	});
	return allowed.has(params.topicId);
}

export function resolveTopicPickerRowStateFromLearningState(params: {
	learningState: TopicLearningState;
	eligibility: TopicPickerEligibility;
	ineligibleDisplay?: TopicPickerIneligibleDisplay;
}): TopicPickerRowState {
	const ineligibleDisplay =
		params.ineligibleDisplay ?? TOPIC_PICKER_INELIGIBLE_DISPLAY.disabled;

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.all) {
		return "selectable";
	}

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.completed) {
		if (params.learningState === TOPIC_LEARNING_STATE.completed) {
			return "selectable";
		}
		if (params.learningState === TOPIC_LEARNING_STATE.started) {
			return ineligibleDisplay === TOPIC_PICKER_INELIGIBLE_DISPLAY.disabled
				? "disabled_incomplete"
				: "hidden";
		}
		return "hidden";
	}

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.started) {
		if (
			params.learningState === TOPIC_LEARNING_STATE.started ||
			params.learningState === TOPIC_LEARNING_STATE.completed
		) {
			return "selectable";
		}
		return "hidden";
	}

	return "hidden";
}

export function resolveTopicPickerRowState(params: {
	topicId: string;
	eligibility: TopicPickerEligibility;
	progress: TopicPickerProgressSnapshot;
	ineligibleDisplay?: TopicPickerIneligibleDisplay;
}): TopicPickerRowState {
	const isCompleted = params.progress.completedTopicIds.has(params.topicId);
	const isStarted = params.progress.startedTopicIds.has(params.topicId);

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.all) {
		return "selectable";
	}

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.completed) {
		if (isCompleted) {
			return "selectable";
		}
		if (isStarted) {
			return resolveTopicPickerRowStateFromLearningState({
				learningState: TOPIC_LEARNING_STATE.started,
				eligibility: TOPIC_PICKER_ELIGIBILITY.completed,
				ineligibleDisplay: params.ineligibleDisplay,
			});
		}
		return "hidden";
	}

	if (params.eligibility === TOPIC_PICKER_ELIGIBILITY.started) {
		if (isStarted || isCompleted) {
			return "selectable";
		}
		return "hidden";
	}

	return "hidden";
}
